// frontend/src/components/mcbim/BuildingStoryList.tsx
import React, { useMemo, useState } from "react";
import {
  ActionIcon,
  Badge,
  Box,
  Card,
  Center,
  Group,
  Loader,
  Menu,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useAppSelector, useAppDispatch } from "../../app/hooks";
import { useGetMcbimBuildingStoriesQuery } from "../../../openapi";
import { MdSort } from "react-icons/md";
import { FiCheck } from "react-icons/fi";
import { setBuildingStoryListRedux, setFloorAndJobInRedux } from "../designSlice";
import { sortArrayByNumbers, sortArrayByStrings } from "../../globalUtils";
import { selectBuildingStoryList } from "../designSelector";

type SortMode = "name" | "rooms";

export const BuildingStoryList: React.FC = () => {
  const dispatch = useAppDispatch();
  const projectNo = useAppSelector((state) => state.design.projectNo);
  const floorAndJob = useAppSelector((state) => state.design.floorAndJob);

  const buildingStoriesState = useAppSelector((state) => state.design.buildingStoryList);

  const {
    data: buildingStories = [],
    isLoading,
    isError,
  } = useGetMcbimBuildingStoriesQuery({ projectNo: projectNo! }, { skip: !projectNo });

  const filteredStories = useAppSelector(selectBuildingStoryList);


  if (isLoading) {
    return (
      <Center style={{ height: "100%" }}>
        <Loader size="sm" />
      </Center>
    );
  }

  if (isError) {
    return (
      <Text size="xs" c="red">
        Kunne ikke laste etasjer.
      </Text>
    );
  }

  if (!buildingStories || buildingStories.length === 0) {
    return (
      <Text size="xs" c="dimmed">
        Ingen etasjer funnet i dette prosjektet.
      </Text>
    );
  }

  const { sortMode } = buildingStoriesState;

  const setSearch = (search: string) => {
    dispatch(
      setBuildingStoryListRedux({
        ...buildingStoriesState,
        search,
      })
    );
  };

  const setSortMode = (sortMode: SortMode) => {
    dispatch(
      setBuildingStoryListRedux({
        ...buildingStoriesState,
        sortMode,
      })
    );
  };

  return (
    <Box
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Søk + sortering */}
      <Group justify="space-between" gap="xs" wrap="nowrap">
        <TextInput
          value={buildingStoriesState.search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          placeholder="Søk på etasjenavn…"
          size="xs"
          style={{ flex: 1 }}
        />

        <Menu withinPortal position="bottom-end">
          <Menu.Target>
            <ActionIcon variant="light" size="sm" aria-label="Sorter etasjer">
              <MdSort />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Label>Sorter etter</Menu.Label>
            <Menu.Item
              onClick={() => setSortMode("name")}
              rightSection={sortMode === "name" ? <FiCheck size={14} /> : null}
            >
              Navn
            </Menu.Item>
            <Menu.Item
              onClick={() => setSortMode("rooms")}
              rightSection={sortMode === "rooms" ? <FiCheck size={14} /> : null}
            >
              Antall rom
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>

      {/* Egen scroll med overflow-auto */}
      <Box
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          paddingRight: 8,
          marginRight: -8,
          scrollbarWidth: "thin",
          scrollbarColor: "#a0a0a0 transparent",
        }}
      >
        <Stack gap={4} pb={4}>
          {filteredStories.map((story) => {
            const storyJobId = String(story.job.jobId);
            const isSelected =
              floorAndJob?.floorName === story.name && floorAndJob?.jobId === storyJobId;

            return (
              <Card
                key={story.index}
                withBorder
                radius="sm"
                p="xs"
                onClick={() =>
                  dispatch(
                    setFloorAndJobInRedux({
                      floorName: story.name,
                      jobId: storyJobId,
                      floorGlobalId: story.globalId,
                    })
                  )
                }
                style={{
                  cursor: "pointer",
                  backgroundColor: isSelected ? "#e7f5ff" : undefined,
                  borderColor: isSelected ? "#228be6" : undefined,
                  transition:
                    "background-color 120ms ease, box-shadow 120ms ease, transform 120ms ease",
                }}
              >
                <Group justify="space-between" gap="xs">
                  <Text size="sm" fw={500} lineClamp={1}>
                    {story.name}
                  </Text>
                  <Badge variant="light" size="xs" px={6}>
                    {story.spaceCount} ROM
                  </Badge>
                </Group>
              </Card>
            );
          })}
        </Stack>
      </Box>
    </Box>
  );
};
